import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Container, Header } from '@amzn/awsui-components-react';
import KeyValuePairs from '@amzn/awsui-components-react/polaris/key-value-pairs';
import { API, Auth } from 'aws-amplify';
import { VALARI_API_NAME } from '../../../../../common/config/api_endpoints';
import { DEFAULT_BUSINESS_ID, DEFAULT_COUNTRY } from '../../../../../common/config/api_constants';

export const CustomDataRenderer = ({
  subKey,
  data,
  questionsData,
  sectionKey,
  surveyId,
  entityId,
  studyPeriod,
}) => {
  const [questions, setQuestions] = useState(null);
  const [artifacts, setArtifacts] = useState({});
  const hasFetchedQuestions = useRef(false);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (
        (subKey === 'questionnaireArtifacts' || sectionKey === 'questionnaireArtifacts') &&
        surveyId &&
        !hasFetchedQuestions.current
      ) {
        hasFetchedQuestions.current = true;
        try {
          const accessToken = (await Auth.currentSession()).getAccessToken().getJwtToken();
          const clientId = DEFAULT_BUSINESS_ID + (entityId || '');

          const response = await API.post(VALARI_API_NAME, '/getQuestions', {
            body: {
              surveyId,
              accessToken,
              clientId,
              country: DEFAULT_COUNTRY,
              studyPeriod,
            },
          });

          if (response?.body?.questions) {
            try {
              const parsedQuestions = JSON.parse(response.body.questions);
              setQuestions(parsedQuestions);
            } catch (parseError) {
              hasFetchedQuestions.current = false;
              console.error('Error parsing questions JSON:', parseError);
            }
          }
        } catch (error) {
          hasFetchedQuestions.current = false; // Reset on error to allow retry
          console.error('Error fetching questions:', error);
        }
      }
    };

    fetchQuestions();
  }, [subKey, sectionKey, surveyId, entityId, studyPeriod]);

  useEffect(() => {
    const fetchArtifacts = async () => {
      if (
        (subKey === 'questionnaireArtifacts' || sectionKey === 'questionnaireArtifacts') &&
        data?.data
      ) {
        const artifactIds = [];
        Object.entries(data.data).forEach(([, sectionData]) => {
          if (sectionData && typeof sectionData === 'object') {
            Object.entries(sectionData).forEach(([, artifactId]) => {
              if (artifactId && typeof artifactId === 'string') {
                artifactIds.push(artifactId);
              }
            });
          }
        });

        if (artifactIds.length > 0) {
          try {
            const accessToken = (await Auth.currentSession()).getAccessToken().getJwtToken();
            const clientId = DEFAULT_BUSINESS_ID + (entityId || '');

            const requestBody = {
              artifactIdList: artifactIds,
              studyPeriod,
              accessToken,
              clientId,
              country: DEFAULT_COUNTRY,
            };

            const response = await API.post(VALARI_API_NAME, '/getArtifactByArtifactId', {
              body: requestBody,
            });

            if (response?.body?.artifactList) {
              const artifactMap = {};
              response.body.artifactList.forEach(artifact => {
                let content;

                // Handle different artifact structures
                if (artifact.artifactData?.answer) {
                  // Human artifacts structure - answer in artifactData
                  content = artifact.artifactData.answer;
                } else if (artifact.answer) {
                  // Human artifacts structure - direct answer field
                  content = artifact.answer;
                } else if (artifact.artifactData?.llmOutput) {
                  // AI artifacts structure
                  content = artifact.artifactData.llmOutput;
                  // Try to extract content from <output> tags if present
                  const outputMatch = content.match(/<output>(.*?)<\/output>/s);
                  if (outputMatch) {
                    content = outputMatch[1].trim();
                  }
                } else {
                  content = artifact.content || JSON.stringify(artifact) || 'No content available';
                }

                artifactMap[artifact.artifactId] = content;
              });
              setArtifacts(artifactMap);
            }
          } catch (error) {
            console.error('Error fetching artifacts:', error);
            console.error('Error details:', error.response || error.message);
          }
        }
      }
    };

    fetchArtifacts();
  }, [subKey, sectionKey, data, entityId, studyPeriod]); // Removed artifacts from dependencies

  // Helper function to create question map
  const createQuestionMap = questionsArray => {
    const map = {};
    if (questionsArray) {
      questionsArray.forEach(q => {
        // Map main question ID to its title
        map[q.questionId] = q.questionTitle;

        if (q.questionType === 'QUESTION_GROUP' && q.subQuestions) {
          q.subQuestions.forEach(sub => {
            map[sub.questionId] = sub.questionTitle || q.questionTitle;
          });
        }
      });
    }
    return map;
  };

  // Handle questionnaire data rendering
  if (
    (subKey === 'questionnaireArtifacts' || sectionKey === 'questionnaireArtifacts') &&
    data?.data
  ) {
    const questionMap = createQuestionMap(questions);

    return (
      <div>
        {Object.entries(data.data).map(([sectionName, sectionData]) => {
          if (!sectionData) return null;

          if (sectionName === 'approvedList') {
            return (
              <div key={sectionName} style={{ marginBottom: '16px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Approved List</div>
                {Object.entries(sectionData).map(([questionId, value]) => {
                  const questionTitle = questionMap[questionId] || questionId;
                  return (
                    <div key={questionId} style={{ marginBottom: '4px', paddingLeft: '16px' }}>
                      <strong>{questionTitle}:</strong> {value.toString()}
                    </div>
                  );
                })}
              </div>
            );
          }

          const getSectionDisplayName = name => {
            if (name === 'human') return 'Human Response';
            if (name === 'genai') return 'AI Response';
            if (name === 'admin') return 'Admin Response';
            if (name === 'approvedList') return 'Approved List';
            return name;
          };

          const displaySectionName = getSectionDisplayName(sectionName);

          return (
            <div key={sectionName} style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{displaySectionName}</div>
              {Object.entries(sectionData).map(([questionId, artifactId]) => {
                // Strip questionnaire_ prefix for mapping
                const cleanQuestionId = questionId.startsWith('questionnaire_')
                  ? questionId.replace('questionnaire_', '')
                  : questionId;
                return (
                  <div key={questionId} style={{ marginBottom: '4px', paddingLeft: '16px' }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {questionMap[cleanQuestionId] || questionId}
                    </div>
                    <div style={{ paddingLeft: '16px', color: '#666' }}>
                      {artifacts[artifactId] || artifactId || 'N/A'}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }
  // Handle questionnaire data rendering
  if (subKey === 'questionnaire' && data?.data) {
    const questionMap = createQuestionMap(questionsData || questions);

    const renderSection = (sectionName, sectionData) => {
      if (!sectionData) return null;

      if (sectionName === 'approvedList') {
        // Group questions by main question number
        const questionGroups = {};
        Object.entries(sectionData).forEach(([questionId, value]) => {
          const cleanQuestionId = questionId.startsWith('questionnaire_')
            ? questionId.replace('questionnaire_', '')
            : questionId;
          const mainQuestion = cleanQuestionId.split('_')[0];

          if (!questionGroups[mainQuestion]) {
            questionGroups[mainQuestion] = [];
          }
          questionGroups[mainQuestion].push(`${cleanQuestionId}: ${value}`);
        });

        const items = Object.entries(questionGroups).map(([mainQ, subQuestions]) => ({
          label: questionMap[mainQ] || mainQ,
          value: subQuestions.join('; '),
        }));

        return (
          <Container header={<Header variant="h4">Approved List</Header>}>
            <KeyValuePairs columns={2} items={items} />
          </Container>
        );
      }

      const getSectionDisplayName = name => {
        if (name === 'human') return 'Human Response';
        if (name === 'genai') return 'AI Response';
        if (name === 'admin') return 'Admin Response';
        if (name === 'approvedList') return 'Approved List';
        return name;
      };

      const displaySectionName = getSectionDisplayName(sectionName);

      // Group questions by main question number (e.g., q2, q3, etc.)
      const questionGroups = {};
      Object.entries(sectionData).forEach(([questionId, value]) => {
        const cleanQuestionId = questionId.startsWith('questionnaire_')
          ? questionId.replace('questionnaire_', '')
          : questionId;
        const mainQuestion = cleanQuestionId.split('_')[0]; // Get q2, q3, etc.

        if (!questionGroups[mainQuestion]) {
          questionGroups[mainQuestion] = [];
        }
        questionGroups[mainQuestion].push(`${cleanQuestionId}: ${value}`);
      });

      // Create items from grouped questions
      const items = Object.entries(questionGroups).map(([mainQ, subQuestions]) => ({
        label: questionMap[mainQ] || mainQ,
        value: subQuestions.join('; '),
      }));

      return (
        <Container header={<Header variant="h4">{displaySectionName}</Header>}>
          <KeyValuePairs columns={2} items={items} />
        </Container>
      );
    };

    return (
      <div>
        {Object.entries(data.data).map(([sectionName, sectionData]) => (
          <div key={sectionName} style={{ marginBottom: '16px' }}>
            {renderSection(sectionName, sectionData)}
          </div>
        ))}
      </div>
    );
  }

  if (subKey === 'addPartners' && data?.data && Array.isArray(data.data)) {
    return (
      <div>
        {data.data.map(item => (
          <div
            key={`${item.recordId}-${item.email}`}
            style={{ marginBottom: '8px', padding: '8px', border: '1px solid #e0e0e0' }}
          >
            <div>
              <strong>Record ID:</strong> {item.recordId}
            </div>
            <div>
              <strong>Email:</strong> {item.email}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (subKey === 'projectScoping') {
    // Handle NAV_BLOCKED error case
    if (data?.error === 'NAV_BLOCKED') {
      return (
        <div style={{ padding: '8px', fontStyle: 'italic' }}>
          Navigation blocked - No projects selected
        </div>
      );
    }

    if (data?.data?.projectScopingList && Array.isArray(data.data.projectScopingList)) {
      return (
        <div>
          {data.data.projectScopingList.map(project => (
            <div
              key={project}
              style={{ marginBottom: '8px', padding: '8px', border: '1px solid #e0e0e0' }}
            >
              <div>
                <strong>Project:</strong> {project}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (data?.data === null || !data?.data?.projectScopingList) {
      return <div style={{ padding: '8px', fontStyle: 'italic' }}>No projects selected</div>;
    }
  }

  if (subKey === 'uploadDocumentation') {
    // Handle NAV_BLOCKED error case
    if (data?.error === 'NAV_BLOCKED') {
      return (
        <div style={{ padding: '8px', fontStyle: 'italic' }}>
          Navigation blocked - No documents selected
        </div>
      );
    }

    if (data?.data && Array.isArray(data.data)) {
      return (
        <div>
          {data.data.map(doc => (
            <div
              key={doc.documentId || doc.documentFileName}
              style={{ marginBottom: '16px', padding: '8px', border: '1px solid #e0e0e0' }}
            >
              <div>
                <strong>Document Name:</strong> {doc.documentFileName}
              </div>
              <div>
                <strong>Document Category:</strong> {doc.documentSubCategory}
              </div>
              <div>
                <strong>Upload Type:</strong> {doc.documentUploadType}
              </div>
            </div>
          ))}
          {data.cannotUploadDocuments && (
            <div
              style={{
                marginTop: '8px',
                padding: '8px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
              }}
            >
              <div>
                <strong>Upload Restricted:</strong> {data.uploadRestrictionReason}
              </div>
              {data.uploadRestrictionAdditionalDetails && (
                <div>
                  <strong>Details:</strong> {data.uploadRestrictionAdditionalDetails}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
  }

  if (subKey === 'freeFormStore') {
    let parsedData = data;

    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch (error) {
        // If parsing fails, use as is
      }
    }

    if (typeof parsedData === 'object' && parsedData !== null) {
      const beautifiedData = {};
      Object.keys(parsedData).forEach(key => {
        const value = parsedData[key];
        if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
          try {
            beautifiedData[key] = JSON.parse(value);
          } catch (error) {
            beautifiedData[key] = value;
          }
        } else {
          beautifiedData[key] = value;
        }
      });

      return (
        <div>
          {Object.keys(beautifiedData).map(key => {
            const value = beautifiedData[key];

            if (key.includes('employeeNames.smes') && Array.isArray(value)) {
              return (
                <div
                  key={key}
                  style={{ marginBottom: '16px', padding: '8px', border: '1px solid #e0e0e0' }}
                >
                  <div>
                    <strong>SMEs:</strong>
                  </div>
                  {value.map(emp => (
                    <div
                      key={emp.recordId || emp.description}
                      style={{
                        marginLeft: '16px',
                        marginTop: '8px',
                        padding: '4px',
                        backgroundColor: '#f5f5f5',
                      }}
                    >
                      <div>
                        <strong>Name:</strong> {emp.label}
                      </div>
                      <div>
                        <strong>Role:</strong> {emp.tags?.join(', ')}
                      </div>
                      <div>
                        <strong>Alias:</strong> {emp.description}
                      </div>
                    </div>
                  ))}
                </div>
              );
            }

            if (key.includes('employeeNames.projectManagers') && Array.isArray(value)) {
              return (
                <div
                  key={key}
                  style={{ marginBottom: '16px', padding: '8px', border: '1px solid #e0e0e0' }}
                >
                  <div>
                    <strong>Project Managers:</strong>
                  </div>
                  {value.map(emp => (
                    <div
                      key={emp.recordId || emp.description}
                      style={{
                        marginLeft: '16px',
                        marginTop: '8px',
                        padding: '4px',
                        backgroundColor: '#f5f5f5',
                      }}
                    >
                      <div>
                        <strong>Name:</strong> {emp.label}
                      </div>
                      <div>
                        <strong>Role:</strong> {emp.tags?.join(', ')}
                      </div>
                      <div>
                        <strong>Alias:</strong> {emp.description}
                      </div>
                    </div>
                  ))}
                </div>
              );
            }

            if (key.includes('employeeNames.dateRange') && typeof value === 'object') {
              return (
                <div
                  key={key}
                  style={{ marginBottom: '16px', padding: '8px', border: '1px solid #e0e0e0' }}
                >
                  <div>
                    <strong>Date Range:</strong>
                  </div>
                  <div style={{ marginLeft: '16px', marginTop: '8px' }}>
                    <div>
                      <strong>From:</strong> {value.from?.quarter} {value.from?.year}
                    </div>
                    <div>
                      <strong>To:</strong> {value.to?.quarter} {value.to?.year}
                    </div>
                  </div>
                </div>
              );
            }

            if (key.includes('employeeNames.error')) {
              return null;
            }

            return (
              <div
                key={key}
                style={{ marginBottom: '16px', padding: '8px', border: '1px solid #e0e0e0' }}
              >
                <div>
                  <strong>{key}:</strong>
                </div>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', marginTop: '4px' }}>
                  {JSON.stringify(value, null, 2)}
                </pre>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <pre
        style={{
          whiteSpace: 'pre-wrap',
          fontSize: '12px',
          wordBreak: 'break-word',
          maxWidth: '100%',
        }}
      >
        {JSON.stringify(parsedData, null, 2)}
      </pre>
    );
  }

  if (subKey === 'substantiationProjects' && data?.substantiationProjectItems) {
    return (
      <div>
        {data.substantiationProjectItems.map(item => (
          <div
            key={item.projectId}
            style={{ marginBottom: '16px', padding: '8px', border: '1px solid #e0e0e0' }}
          >
            <div>
              <strong>Project Name:</strong> {item.projectName}
            </div>
            <div>
              <strong>Project ID:</strong> {item.projectId}
            </div>
            {item.projectDescription && (
              <div>
                <strong>Project Description:</strong>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', marginTop: '4px' }}>
                  {(() => {
                    try {
                      return JSON.stringify(JSON.parse(item.projectDescription), null, 2);
                    } catch (e) {
                      return item.projectDescription; // Fallback to raw string
                    }
                  })()}
                </pre>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (subKey === 'substantiationProjectDetails' && data?.substantiationProjectDetailsItems) {
    return (
      <div>
        {data.substantiationProjectDetailsItems.map(item => (
          <div
            key={item.surveyId}
            style={{ marginBottom: '16px', padding: '8px', border: '1px solid #e0e0e0' }}
          >
            <div>
              <strong>Project Name:</strong> {item.projectName}
            </div>
            <div>
              <strong>Project ID:</strong> {item.projectId}
            </div>
            <div>
              <strong>Substantiation Provider ID:</strong> {item.substantiationProviderId}
            </div>
            <div>
              <strong>Substantiation Primary Owner:</strong> {item.substantiationPrimaryOwner}
            </div>
            <div>
              <strong>Survey ID:</strong> {item.surveyId}
            </div>
            <div>
              <strong>Survey Status:</strong>{' '}
              {item.substantiationProjectSurveyStatus || 'Survey not created'}
            </div>
            <div>
              <strong>Owner As Collaborator:</strong>{' '}
              {item.substantiationOwnerAsCollaborator ? 'Yes' : 'No'}
            </div>
            {item.note && (
              <div>
                <strong>Note:</strong> {item.note}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: '100%' }}>
      {data === null || data === undefined || data === ''
        ? 'No Data'
        : JSON.stringify(data, null, 2)}
    </pre>
  );
};

CustomDataRenderer.propTypes = {
  subKey: PropTypes.string,
  data: PropTypes.any,
  questionsData: PropTypes.array,
  sectionKey: PropTypes.string,
  surveyId: PropTypes.string,
  entityId: PropTypes.string,
  studyPeriod: PropTypes.string,
};

export const getSectionHeading = (sectionKey, subKey = null) => {
  const headingMap = {
    status: 'Status',
    'userResponse.projectScoping': 'Project Scoping',
    'userResponse.activeStep': 'Active Step',
    'userResponse.uploadDocumentation': 'Selected Documents',
    'userResponse.addPartners': 'Survey Partners(Secondary Assignee)',
    'userResponse.freeFormStore': 'Additional Information',
    'userResponse.questionnaireArtifacts': 'Questionnaire',
    secondaryAssignees: 'Survey Partners(Secondary Assignee)',
    'userResponse.substantiationProjects': 'Selected Projects',
    'userResponse.substantiationProjectDetails': 'Substantiation Survey Status',
  };

  const key = subKey ? `${sectionKey}.${subKey}` : sectionKey;
  return headingMap[key] || key;
};

getSectionHeading.propTypes = {
  sectionKey: PropTypes.string.isRequired,
  subKey: PropTypes.string,
};
